import React from "react";
import {
  Box,
  Button,
  CircularProgress,
  Modal,
  Typography,
  TextField,
  FormControl,
  FormLabel,
  Stack,
} from "@mui/material";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import {
  ModalBaseStyles,
  ModalActionButtonStyles,
  ModalHeader,
} from "../../Common/styles/modal";
import { InputWrapper, LabelStyle } from "../../Common/styles/form";

interface Study {
  id: string;
  name: string;
}

interface SaveCohortModalProps {
  open: boolean;
  onClose: () => void;
  onSave: (data: SaveCohortData, action: "create" | "update") => void;
  isLoading?: boolean;
  allStudies?: Study[];
  loadedCohort?: {
    id: string;
    name: string;
    description: string;
  } | null;
}

interface SaveCohortData {
  name: string;
  description: string;
  // visibility: "private" | "team" | "org";
  // type: "registry" | "study";
  // studyId?: string;
}

const validationSchema = Yup.object().shape({
  name: Yup.string().required("Name is required"),
  description: Yup.string(),
  // visibility: Yup.string().oneOf(["private", "team", "org"]).required(),
  // type: Yup.string().oneOf(["registry", "study"]).required(),
  // studyId: Yup.string().when("type", {
  //   is: "study",
  //   then: (schema) =>
  //     schema.required("Study is required for study-scoped cohorts"),
  //   otherwise: (schema) => schema,
  // }),
});

const SaveCohortModal: React.FC<SaveCohortModalProps> = ({
  open,
  onClose,
  onSave,
  isLoading = false,
  loadedCohort = null,
}) => {
  const initialValues: SaveCohortData = {
    name: loadedCohort?.name || "",
    description: loadedCohort?.description || "",
    // visibility: "private",
    // type: "registry",
    // studyId: "",
  };

  const handleClose = () => {
    onClose();
  };

  return (
    <Modal open={open} onClose={handleClose}>
      <Box sx={{ ...ModalBaseStyles, minHeight: "50vh" }}>
        <ModalHeader
          title="Save Criteria Definition"
          onCloseClick={handleClose}
        />

        <Typography variant="body1" sx={{ mb: 3, color: "text.secondary" }}>
          Save this criteria for reuse.
        </Typography>

        <Formik
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={(values) => {
            onSave(values, "create");
          }}
        >
          {({ values, errors, touched, getFieldProps, submitForm }) => (
            <Form>
              <Stack>
                {/* Name Field */}
                <FormControl sx={InputWrapper}>
                  <FormLabel sx={LabelStyle}>
                    Name <span style={{ color: "#F05252" }}>*</span>
                  </FormLabel>
                  <TextField
                    {...getFieldProps("name")}
                    fullWidth
                    placeholder="e.g., High BMI Diabetes Patients"
                    error={touched.name && Boolean(errors.name)}
                    helperText={touched.name && errors.name ? errors.name : " "}
                    data-testid="input-cohort-name"
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: "8px",
                      },
                    }}
                  />
                </FormControl>

                {/* Description Field */}
                <FormControl sx={InputWrapper}>
                  <FormLabel sx={LabelStyle}>Description</FormLabel>
                  <TextField
                    {...getFieldProps("description")}
                    fullWidth
                    multiline
                    rows={3}
                    placeholder="Optional description of this Criteria..."
                    data-testid="input-cohort-description"
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: "8px",
                      },
                    }}
                  />
                </FormControl>

                {/* Visibility Field */}
                {/* <FormControl sx={InputWrapper}>
                  <FormLabel sx={LabelStyle}>Visibility</FormLabel>
                  <Select
                    {...getFieldProps("visibility")}
                    fullWidth
                    data-testid="select-visibility"
                    sx={{
                      borderRadius: "8px",
                    }}
                  >
                    <MenuItem value="private" data-testid="option-private">
                      Private - Only visible to you
                    </MenuItem>
                    <MenuItem value="team" data-testid="option-team">
                      Team - Visible to your team
                    </MenuItem>
                    <MenuItem value="org" data-testid="option-org">
                      Organization - Visible to all
                    </MenuItem>
                  </Select>
                </FormControl> */}

                {/* Cohort Type Field */}
                {/* <FormControl sx={InputWrapper}>
                  <FormLabel sx={LabelStyle}>Cohort Type</FormLabel>
                  <Select
                    {...getFieldProps("type")}
                    fullWidth
                    data-testid="select-type"
                    sx={{
                      borderRadius: "8px",
                    }}
                  >
                    <MenuItem value="registry" data-testid="option-registry">
                      Registry-wide - Across all studies
                    </MenuItem>
                    <MenuItem value="study" data-testid="option-study">
                      Study-scoped - Limited to one study
                    </MenuItem>
                  </Select>
                </FormControl> */}

                {/* Study Selection (conditional) */}
                {/* {values.type === "study" && (
                  <FormControl sx={InputWrapper}>
                    <FormLabel sx={LabelStyle}>
                      Study <span style={{ color: "#F05252" }}>*</span>
                    </FormLabel>
                    <Select
                      {...getFieldProps("studyId")}
                      fullWidth
                      error={touched.studyId && Boolean(errors.studyId)}
                      data-testid="select-study"
                      sx={{
                        borderRadius: "8px",
                      }}
                    >
                      {allStudies.map((study) => (
                        <MenuItem
                          key={study.id}
                          value={study.id}
                          data-testid={`option-study-${study.id}`}
                        >
                          {study.name}
                        </MenuItem>
                      ))}
                    </Select>
                    <Typography
                      variant="caption"
                      color="error"
                      sx={{ mt: 0.5, ml: 1.75 }}
                    >
                      {touched.studyId && errors.studyId ? errors.studyId : " "}
                    </Typography>
                  </FormControl>
                )} */}
              </Stack>

              {/* Action Buttons */}
              <Box sx={{ ...ModalActionButtonStyles, mt: 4 }}>
                <Button
                  variant="outlined"
                  onClick={handleClose}
                  disabled={isLoading}
                  data-testid="button-cancel-save"
                  sx={{
                    borderRadius: "8px",
                    textTransform: "none",
                    fontWeight: 600,
                    px: 3,
                  }}
                >
                  Cancel
                </Button>

                {loadedCohort ? (
                  // Show two buttons for loaded cohort
                  <>
                    <Button
                      type="button"
                      variant="outlined"
                      disabled={isLoading || !values.name?.trim()}
                      onClick={() => {
                        onSave(values, "create");
                      }}
                      data-testid="button-save-as-new"
                      sx={{
                        borderRadius: "8px",
                        textTransform: "none",
                        fontWeight: 600,
                        px: 3,
                      }}
                    >
                      {isLoading ? (
                        <Box
                          sx={{ display: "flex", alignItems: "center", gap: 1 }}
                        >
                          <CircularProgress size={16} color="inherit" />
                          Saving...
                        </Box>
                      ) : (
                        "Save and Create New"
                      )}
                    </Button>
                    <Button
                      type="button"
                      variant="contained"
                      disabled={isLoading || !values.name?.trim()}
                      onClick={() => {
                        onSave(values, "update");
                      }}
                      data-testid="button-save-update"
                      sx={{
                        borderRadius: "8px",
                        textTransform: "none",
                        fontWeight: 600,
                        px: 3,
                      }}
                    >
                      {isLoading ? (
                        <Box
                          sx={{ display: "flex", alignItems: "center", gap: 1 }}
                        >
                          <CircularProgress size={16} color="inherit" />
                          Updating...
                        </Box>
                      ) : (
                        "Save and Update"
                      )}
                    </Button>
                  </>
                ) : (
                  // Show single button for new cohort
                  <Button
                    type="button"
                    variant="contained"
                    disabled={isLoading || !values.name?.trim()}
                    onClick={() => {
                      onSave(values, "create");
                    }}
                    data-testid="button-confirm-save"
                    sx={{
                      borderRadius: "8px",
                      textTransform: "none",
                      fontWeight: 600,
                      px: 3,
                    }}
                  >
                    {isLoading ? (
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 1 }}
                      >
                        <CircularProgress size={16} color="inherit" />
                        Saving...
                      </Box>
                    ) : (
                      "Save Criteria"
                    )}
                  </Button>
                )}
              </Box>
            </Form>
          )}
        </Formik>
      </Box>
    </Modal>
  );
};

export default SaveCohortModal;
